import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowPathIcon,
  CalendarDaysIcon,
  ChartBarSquareIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  QrCodeIcon,
  TicketIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import scannerApi from "../../apis/scanner.api";
import programmsOrganizerApi from "../../apis/organiserApis/programms.api";
import MetaData from "../../component/Metadata";

const STATUS_FILTERS = [
  ["all", "All"],
  ["valid", "Valid"],
  ["used", "Checked in"],
  ["attendee_pending", "Attendee pending"],
  ["void", "Void"],
  ["refunded", "Refunded"],
];

const getBody = (response) => {
  const body = response?.data || {};
  if (body?.data && !body.summary && !body.tickets) return body.data;
  return body;
};

const attendeeName = (attendee) =>
  attendee?.name ||
  [attendee?.firstName, attendee?.lastName].filter(Boolean).join(" ") ||
  "";

const formatDateTime = (value) => {
  if (!value) return "Not scanned";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const statusLabel = (status) => {
  const labels = {
    valid: "Valid",
    used: "Checked in",
    attendee_pending: "Attendee pending",
    void: "Void",
    refunded: "Refunded",
  };
  return labels[String(status || "").toLowerCase()] || status || "Ticket";
};

const statusClass = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "used") {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }
  if (normalized === "valid") {
    return "border-blue-200 bg-blue-50 text-blue-700";
  }
  if (normalized === "attendee_pending") {
    return "border-amber-200 bg-amber-50 text-amber-800";
  }
  return "border-red-200 bg-red-50 text-red-700";
};

const contentOption = (item, type) => ({
  contentId:
    item.content_id ||
    item.contentId ||
    item.event_id ||
    item.movie_id ||
    "",
  contentType: String(item.type || item.contentType || type || "event").toLowerCase(),
  title: item.title || item.name || item.event_name || item.movie_name || "Untitled",
  date:
    item.start_date_time ||
    item.startDate ||
    item.start_date ||
    item.event_date ||
    "",
});

function SummaryItem({ label, value, icon: Icon, tone = "text-gray-950" }) {
  return (
    <div
      role="group"
      aria-label={`${label}: ${value ?? 0}`}
      className="rounded-lg border border-gray-200 bg-white p-4"
    >
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
          {label}
        </p>
        <Icon className="h-5 w-5 text-gray-400" />
      </div>
      <p className={`mt-3 text-2xl font-black ${tone}`}>{value ?? 0}</p>
    </div>
  );
}

function StatusBadge({ status }) {
  return (
    <span
      className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-bold ${statusClass(
        status,
      )}`}
    >
      {statusLabel(status)}
    </span>
  );
}

function ContentPicker({ accessMode, onOpen }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");

    if (accessMode === "organizer") {
      Promise.all([
        new Promise((resolve, reject) => {
          programmsOrganizerApi.listEventsByOrganizer({
            payload: { page: 1, limit: 100 },
            success: resolve,
            error: reject,
          });
        }),
        new Promise((resolve, reject) => {
          programmsOrganizerApi.getMovieListofOrganizer({
            payload: { page: 1, limit: 100 },
            success: resolve,
            error: reject,
          });
        }),
      ])
        .then(([eventResponse, movieResponse]) => {
          if (!active) return;
          const eventBody = eventResponse?.data?.data || {};
          const movieBody = movieResponse?.data || {};
          const events = eventBody?.events || eventBody?.data?.events || [];
          const movies = movieBody?.data || [];
          setItems([
            ...events.map((item) => contentOption(item, "event")),
            ...movies.map((item) => contentOption(item, "movie")),
          ]);
        })
        .catch((requestError) => {
          if (!active) return;
          setError(
            requestError?.response?.data?.message ||
              "Unable to load your events and movies.",
          );
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    } else {
      scannerApi.eventList({
        params: { page: 1, limit: 100 },
        success: (response) => {
          if (!active) return;
          const body = getBody(response);
          const list = Array.isArray(body) ? body : body?.data || [];
          setItems(list.map((item) => contentOption(item, item.type)));
          setLoading(false);
        },
        error: (requestError) => {
          if (!active) return;
          setError(
            requestError?.response?.data?.message ||
              "Unable to load assigned events and movies.",
          );
          setLoading(false);
        },
      });
    }

    return () => {
      active = false;
    };
  }, [accessMode]);

  const visibleItems = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return items;
    return items.filter((item) =>
      `${item.title} ${item.contentType}`.toLowerCase().includes(normalized),
    );
  }, [items, query]);

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-5">
        <h1 className="text-2xl font-black text-gray-950">Check-in dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Choose an event or movie to view live entry activity.
        </p>
      </div>

      <div className="relative mb-5">
        <MagnifyingGlassIcon className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
        <input
          aria-label="Search accessible events and movies"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search events and movies"
          className="w-full rounded-md border border-gray-300 bg-white py-3 pl-11 pr-4 text-sm outline-none focus:border-[#1d275f] focus:ring-2 focus:ring-[#1d275f]/10"
        />
      </div>

      {loading ? (
        <div className="rounded-lg border border-gray-200 bg-white p-10 text-center text-sm font-semibold text-gray-500">
          Loading accessible content...
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">
          {error}
        </div>
      ) : visibleItems.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-10 text-center text-sm font-semibold text-gray-500">
          No accessible events or movies found.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {visibleItems.map((item) => (
            <button
              key={`${item.contentType}-${item.contentId}`}
              type="button"
              onClick={() => onOpen(item)}
              className="flex min-w-0 items-center justify-between gap-4 rounded-lg border border-gray-200 bg-white p-4 text-left hover:border-[#1d275f]/40 hover:shadow-sm"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="rounded bg-gray-100 px-2 py-1 text-[10px] font-black uppercase text-gray-600">
                    {item.contentType}
                  </span>
                  {item.date && (
                    <span className="truncate text-xs text-gray-500">
                      {formatDateTime(item.date)}
                    </span>
                  )}
                </div>
                <p className="mt-2 break-words font-bold text-gray-950">
                  {item.title}
                </p>
              </div>
              <ChevronRightIcon className="h-5 w-5 shrink-0 text-gray-400" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CheckInDashboard({ accessMode = "scanner" }) {
  const navigate = useNavigate();
  const { contentType, contentId } = useParams();
  const normalizedType = String(contentType || "").toLowerCase();
  const hasContent =
    Boolean(contentId) && ["event", "movie"].includes(normalizedType);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("all");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!hasContent) return;

    let active = true;
    setLoading(true);
    setError("");

    scannerApi.getCheckInDashboard({
      contentType: normalizedType,
      contentId,
      accessMode,
      params: {
        page,
        limit: 50,
        recentLimit: 12,
        search: debouncedQuery || undefined,
        status,
      },
      success: (response) => {
        if (!active) return;
        const body = response?.data || {};
        setData({
          ...(body?.data || {}),
          meta: body?.meta || {},
        });
        setLoading(false);
      },
      error: (requestError) => {
        if (!active) return;
        const statusCode = requestError?.response?.status;
        setError(
          accessMode === "organizer" && [401, 403].includes(statusCode)
            ? "Your organizer account does not have access to this check-in dashboard."
            : requestError?.response?.data?.message ||
                "Unable to load the check-in dashboard.",
        );
        setLoading(false);
      },
    });

    return () => {
      active = false;
    };
  }, [
    accessMode,
    contentId,
    debouncedQuery,
    hasContent,
    normalizedType,
    page,
    refreshKey,
    status,
  ]);

  const openContent = (item) => {
    const base =
      accessMode === "organizer" ? "/organizer/check-in" : "/scanner/dashboard";
    navigate(`${base}/${item.contentType}/${item.contentId}`);
  };

  if (!hasContent) {
    return (
      <>
        <MetaData title="Check-in Dashboard | TicketVerz" />
        <ContentPicker accessMode={accessMode} onOpen={openContent} />
      </>
    );
  }

  const summary = data?.summary || {};
  const content = data?.content || {};
  const tickets = data?.tickets || [];
  const recentTicketScans = data?.recentTicketScans || [];
  const recentBookingScans = data?.recentBookingScans || [];
  const meta = data?.meta || {};
  const checkedInPercent = Math.max(
    0,
    Math.min(100, Number(summary.checkedInPercent || 0)),
  );

  return (
    <>
      <MetaData title={`${content.title || "Check-in"} | TicketVerz`} />
      <div className="mx-auto max-w-7xl space-y-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <button
              type="button"
              onClick={() =>
                navigate(
                  accessMode === "organizer"
                    ? "/organizer/check-in"
                    : "/scanner/dashboard",
                )
              }
              className="mb-2 inline-flex items-center gap-1 text-sm font-bold text-[#1d275f]"
            >
              <ChevronLeftIcon className="h-4 w-4" />
              Change event
            </button>
            <h1 className="break-words text-2xl font-black text-gray-950">
              {content.title || "Check-in dashboard"}
            </h1>
            <p className="mt-1 text-sm capitalize text-gray-500">
              {content.contentType || normalizedType} · Live entry overview
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {accessMode === "scanner" && (
              <button
                type="button"
                onClick={() =>
                  navigate(
                    `/scanner/scan?content_id=${encodeURIComponent(
                      contentId,
                    )}&type=${normalizedType}`,
                    { state: { content } },
                  )
                }
                className="inline-flex items-center gap-2 rounded-md bg-[#1d275f] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#151d49]"
              >
                <QrCodeIcon className="h-5 w-5" />
                Open scanner
              </button>
            )}
            <button
              type="button"
              onClick={() => setRefreshKey((current) => current + 1)}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-bold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <ArrowPathIcon
                className={`h-5 w-5 ${loading ? "animate-spin" : ""}`}
              />
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>

        {error && (
          <div
            role="alert"
            className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700"
          >
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
            {error}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
          <SummaryItem
            label="Bookings"
            value={summary.totalBookings}
            icon={CalendarDaysIcon}
          />
          <SummaryItem
            label="Tickets"
            value={summary.totalTickets}
            icon={TicketIcon}
          />
          <SummaryItem
            label="Checked in"
            value={summary.checkedInTickets}
            icon={CheckCircleIcon}
            tone="text-emerald-700"
          />
          <SummaryItem
            label="Remaining"
            value={summary.remainingTickets}
            icon={ClockIcon}
            tone="text-blue-700"
          />
          <SummaryItem
            label="Progress"
            value={`${checkedInPercent}%`}
            icon={ChartBarSquareIcon}
          />
          <SummaryItem
            label="Pending"
            value={summary.attendeePending}
            icon={UserGroupIcon}
            tone={summary.attendeePending ? "text-amber-700" : "text-gray-950"}
          />
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex justify-between gap-4 text-sm">
            <span className="font-bold text-gray-900">Check-in progress</span>
            <span className="font-black text-[#1d275f]">
              {summary.checkedInTickets || 0} / {summary.totalTickets || 0}
            </span>
          </div>
          <div className="mt-3 h-3 overflow-hidden rounded-full bg-gray-100">
            <div
              className="h-full rounded-full bg-emerald-500 transition-all"
              style={{ width: `${checkedInPercent}%` }}
            />
          </div>
        </div>

        <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-100 p-4 sm:p-5">
            <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
              <div>
                <h2 className="font-black text-gray-950">Tickets</h2>
                <p className="mt-1 text-xs text-gray-500">
                  Search booking, attendee, ticket name, ticket ID, or seat.
                </p>
              </div>
              <div className="flex min-w-0 flex-col gap-3 sm:flex-row">
                <div className="relative min-w-0 sm:w-80">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <input
                    aria-label="Search tickets"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search tickets"
                    className="w-full rounded-md border border-gray-300 py-2.5 pl-10 pr-3 text-sm outline-none focus:border-[#1d275f]"
                  />
                </div>
                <select
                  aria-label="Ticket status"
                  value={status}
                  onChange={(event) => {
                    setStatus(event.target.value);
                    setPage(1);
                  }}
                  className="rounded-md border border-gray-300 bg-white px-3 py-2.5 text-sm font-semibold outline-none focus:border-[#1d275f]"
                >
                  {STATUS_FILTERS.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {loading && !data ? (
            <div
              role="status"
              className="p-10 text-center text-sm font-semibold text-gray-500"
            >
              Loading check-in data...
            </div>
          ) : tickets.length === 0 ? (
            <div className="p-10 text-center text-sm font-semibold text-gray-500">
              No tickets match this view.
            </div>
          ) : (
            <>
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full min-w-[860px] border-collapse text-left">
                  <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-5 py-3">Ticket</th>
                      <th className="px-5 py-3">Attendee</th>
                      <th className="px-5 py-3">Booking</th>
                      <th className="px-5 py-3">Status</th>
                      <th className="px-5 py-3">Scanned</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {tickets.map((row, index) => (
                      <tr
                        key={
                          row.ticket?.ticketInstanceId ||
                          `${row.booking?.bookingId}-${index}`
                        }
                        className="align-top hover:bg-gray-50/70"
                      >
                        <td className="px-5 py-4">
                          <p className="max-w-xs break-words text-sm font-bold text-gray-950">
                            {row.ticket?.ticketName || "Ticket"}
                          </p>
                          <p className="mt-1 text-xs text-gray-500">
                            {row.ticket?.seatNo
                              ? `Seat ${row.ticket.seatNo}`
                              : "General admission"}
                          </p>
                        </td>
                        <td className="px-5 py-4">
                          <p className="text-sm font-semibold text-gray-900">
                            {attendeeName(row.ticket?.attendee) || "Not provided"}
                          </p>
                          {row.ticket?.attendee?.email && (
                            <p className="mt-1 text-xs text-gray-500">
                              {row.ticket.attendee.email}
                            </p>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <p className="text-sm font-semibold text-gray-900">
                            {row.booking?.bookingId}
                          </p>
                          <p className="mt-1 text-xs text-gray-500">
                            {row.booking?.buyerName}
                          </p>
                        </td>
                        <td className="px-5 py-4">
                          <StatusBadge status={row.ticket?.status} />
                        </td>
                        <td className="px-5 py-4">
                          <p className="text-xs font-semibold text-gray-700">
                            {formatDateTime(row.scannedAt)}
                          </p>
                          {row.scannedBy && (
                            <p className="mt-1 text-xs text-gray-500">
                              {row.scannedBy.name || row.scannedBy.email}
                            </p>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="divide-y divide-gray-100 md:hidden">
                {tickets.map((row, index) => (
                  <div
                    key={
                      row.ticket?.ticketInstanceId ||
                      `${row.booking?.bookingId}-${index}`
                    }
                    className="space-y-3 p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="break-words font-bold text-gray-950">
                          {row.ticket?.ticketName || "Ticket"}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          {row.ticket?.seatNo
                            ? `Seat ${row.ticket.seatNo}`
                            : row.booking?.bookingId}
                        </p>
                      </div>
                      <StatusBadge status={row.ticket?.status} />
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <p className="font-bold uppercase text-gray-400">
                          Attendee
                        </p>
                        <p className="mt-1 font-semibold text-gray-800">
                          {attendeeName(row.ticket?.attendee) || "Not provided"}
                        </p>
                      </div>
                      <div>
                        <p className="font-bold uppercase text-gray-400">
                          Scanned
                        </p>
                        <p className="mt-1 font-semibold text-gray-800">
                          {formatDateTime(row.scannedAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3">
            <p className="text-xs font-semibold text-gray-500">
              Page {meta.page || page} of {meta.totalPages || 1} ·{" "}
              {meta.total || 0} tickets
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                className="rounded-md border border-gray-300 p-2 text-gray-600 disabled:opacity-40"
                aria-label="Previous page"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
              <button
                type="button"
                disabled={page >= Number(meta.totalPages || 1)}
                onClick={() => setPage((current) => current + 1)}
                className="rounded-md border border-gray-300 p-2 text-gray-600 disabled:opacity-40"
                aria-label="Next page"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </section>

        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          <section className="rounded-lg border border-gray-200 bg-white p-4 sm:p-5">
            <h2 className="font-black text-gray-950">Recent ticket scans</h2>
            <div className="mt-4 space-y-3">
              {recentTicketScans.length === 0 ? (
                <p className="text-sm text-gray-500">No recent ticket scans.</p>
              ) : (
                recentTicketScans.map((row, index) => (
                  <div
                    key={
                      row.ticket?.ticketInstanceId ||
                      `${row.scannedAt}-${index}`
                    }
                    className="flex items-start justify-between gap-3 border-b border-gray-100 pb-3 last:border-0 last:pb-0"
                  >
                    <div className="min-w-0">
                      <p className="break-words text-sm font-bold text-gray-900">
                        {attendeeName(row.ticket?.attendee) ||
                          row.ticket?.ticketName ||
                          "Ticket"}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        {row.ticket?.ticketName}
                        {row.ticket?.seatNo ? ` · ${row.ticket.seatNo}` : ""}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-xs font-semibold text-gray-700">
                        {formatDateTime(row.scannedAt)}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        {row.scannedBy?.name || row.scannedBy?.email}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-4 sm:p-5">
            <h2 className="font-black text-gray-950">
              Recent legacy booking scans
            </h2>
            <div className="mt-4 space-y-3">
              {recentBookingScans.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No recent legacy booking scans.
                </p>
              ) : (
                recentBookingScans.map((scan, index) => (
                  <div
                    key={scan.id || `${scan.scannedAt}-${index}`}
                    className="flex items-start justify-between gap-3 border-b border-gray-100 pb-3 last:border-0 last:pb-0"
                  >
                    <div>
                      <p className="text-sm font-bold text-gray-900">
                        {scan.bookingId}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        {scan.scannedCount || 0} checked in
                        {scan.partial ? " · Partial" : ""}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-semibold text-gray-700">
                        {formatDateTime(scan.scannedAt)}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        {scan.scannedBy?.name || scan.scannedBy?.email}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>
        </div>
      </div>
    </>
  );
}
